import React from "react";
import { IdCard, ShieldCheck, Landmark, Clock, Lock, Loader2, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function PayoutSetupDialog({ open, onOpenChange, onContinue, loading = false, locale = "en" }) {
  const L = (en, es) => (locale === "es" ? es : en);

  const items = [
    { Icon: IdCard, label: L("A photo ID", "Una identificación con foto") },
    { Icon: ShieldCheck, label: L("Your Social Security number", "Tu número de Seguro Social") },
    { Icon: Landmark, label: L("Your bank account details", "Los datos de tu cuenta bancaria") },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="payout-explainer-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {L("Setting up your payouts", "Configurando tus pagos")}
          </DialogTitle>
          <DialogDescription className="text-base text-slate-600 leading-relaxed">
            {L("You're about to set up your payouts with Stripe, our payments provider.",
               "Vas a configurar tus pagos con Stripe, nuestro proveedor de pagos.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3" data-testid="payout-explainer-checklist">
          <div className="text-sm font-semibold text-slate-800">
            {L("You'll need:", "Necesitas:")}
          </div>
          {items.map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <span className="w-9 h-9 rounded-lg bg-[#0EA5A4]/15 grid place-items-center shrink-0">
                <Icon className="w-4 h-4 text-[#0F766E]" />
              </span>
              <span className="text-sm text-slate-800">{label}</span>
            </div>
          ))}

          <div className="flex items-center gap-2 text-sm text-slate-600 pt-1">
            <Clock className="w-4 h-4 text-slate-400 shrink-0" />
            {L("It takes about 5 minutes.", "Toma unos 5 minutos.")}
          </div>
          <div className="flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900">
            <Lock className="w-4 h-4 mt-0.5 shrink-0" />
            <span data-testid="payout-explainer-privacy">
              {L("Ever Clean Home never sees this information — it goes directly to Stripe.",
                 "Ever Clean Home nunca ve esta información — va directo a Stripe.")}
            </span>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}
            className="rounded-xl" data-testid="payout-explainer-cancel-btn">
            {L("Not now", "Ahora no")}
          </Button>
          <Button onClick={onContinue} disabled={loading}
            className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white transition-colors"
            data-testid="payout-explainer-continue-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>{L("Continue to Stripe", "Continuar a Stripe")}<ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
